import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import useFetch from "../useFetch";
import { ToastContext } from "src/context/toastContext";

import { AddCaseProps, CaseDoctorIOveraProps, CaseDoctorProps, PatientAttributes } from "src/pages/addCase/types";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastTypes } from "src/types";
import { AddCaseResponseProps } from "./types";
import { AuthContext } from "src/context/authContext";
import { PracticeDropdownResponse } from "src/components/caseStepTwo/types";
import { CATEGORY_SELECTION_OPTIONS } from "src/constants/cases";



/**
 * @AddNewCase form handling for case data submission
 * @fourth case submit handler
 */
const useCaseSubmitFunc = () => {
  const navigate = useNavigate()
  const { showToast } = useContext(ToastContext)
  const { userData } = useContext(AuthContext)

  const { data: practiceDropdownApiRes } =
    useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN, { search_query: "" }, {}, ["Practice", "Contact"].includes(userData?.ref_type || ""));




  const { loading: addCaseLoading, data: addCaseData, mutate: addNewCase } =
    useFetch<AddCaseResponseProps>(APIEndpoints.ADD_NEW_CASE);

  useEffect(() => {
    if (addCaseData && addCaseData.status === 200) {
      localStorage.removeItem('newCase');
      showToast([`Your request submitted successfully!`], ToastTypes.SUCCESS);
      if (addCaseData.case_id) {
        if (addCaseData.precert_id) {
          navigate(
            `/cases/${addCaseData.case_id}/precerts/${addCaseData.precert_id}/edit`,
            { state: { drug_name: addCaseData.drug_name } }
          );
        } else {
          navigate(`/cases/${addCaseData.case_id}/documents/new`,
            { state: { drug_name: addCaseData.drug_name } }
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCaseData]);

  /** @fourth */
  const caseDataSubmission = (values: AddCaseProps) => {
    const prescription_attributes_attributes = [];

    if (values.prescription_attributes.scheduled_date_of_service) {
      prescription_attributes_attributes.push({
        name: "scheduled_date_of_service",
        value: values.prescription_attributes.scheduled_date_of_service
      });
    }

    if (values.prescription_attributes.surgical_date) {
      prescription_attributes_attributes.push({
        name: "surgical_date",
        value: values.prescription_attributes.surgical_date,
      })
    }
     if (values.prescription_attributes.has_been_treated) {
      prescription_attributes_attributes.push({
        name: "has_been_treated",
        value: values.prescription_attributes.has_been_treated,
      })
    }

    if (values.prescription_attributes.injection_site) {
      prescription_attributes_attributes.push({
        name: "injection_site",
        value: values.prescription_attributes.injection_site.replace(/Knee/gi, "").trim().toLowerCase(),
      });
    }

    if (values.prescription_attributes.last_date_of_service || values.prescription_attributes.last_surgical_date) {
      prescription_attributes_attributes.push({
        name: values.prescription_attributes.drug_name === "iovera" || values.prescription_attributes.drug_name === "iovera°" ? "last_surgical_date" : "last_date_of_service",
        value: values.prescription_attributes.drug_name === "iovera" || values.prescription_attributes.drug_name === "iovera°" ? values.prescription_attributes.last_surgical_date : values.prescription_attributes.last_date_of_service
      });
    }

    if (values.prescription_attributes.treatment_date) {
      prescription_attributes_attributes.push({
        name: "treatment_date",
        value: values.prescription_attributes.treatment_date
      });
    }

    if (values.prescription_attributes.nerves_qty) {
      prescription_attributes_attributes.push({
        name: "nerves_qty",
        value: values.prescription_attributes.nerves_qty
      });
    }

    if (values.prescription_attributes.other_nerves && values.prescription_attributes.other_nerves.length > 0) {
      prescription_attributes_attributes.push({
        name: "additional_nerves",
        value: values.prescription_attributes.other_nerves
      })
    }

    if (values.prescription_attributes.nerves) {
      prescription_attributes_attributes.push({
        name: "nerves",
        value: values.prescription_attributes.nerves.join(","),
      });
    }

    const { other_nerves, injection_site, nerves, nerves_qty, has_been_treated, ...actualPrescription } = values.prescription_attributes;

    const { global_doctor_id, ...remaining_doctor_attributes } = values.doctor_attributes as CaseDoctorProps;
    const { i_am, treating_doctor_id, is_anesthesiologist, referring_practice_id, referring_doctor_id, ...actual_doctor_attributes } = remaining_doctor_attributes as unknown as CaseDoctorIOveraProps;

    const case_attributes_attributes = []

    if (Object.hasOwn(values.doctor_attributes, "i_am")) {
      case_attributes_attributes.push({
        name: "i_am",
        value: (values.doctor_attributes as CaseDoctorIOveraProps).i_am
      })
    }

    if (values.prescription_attributes.drug_name.toLowerCase().includes("zil")) {
      case_attributes_attributes.push({
        name: "hcp_benefit_selection",
        value: CATEGORY_SELECTION_OPTIONS[values.category as keyof typeof CATEGORY_SELECTION_OPTIONS] || "No Selection Made",
      })
    }

    let submitting_practice = 0;
    if (values.prescription_attributes.drug_name.toLowerCase().includes("zil")) {
      submitting_practice = (values.doctor_attributes as CaseDoctorProps).practice_id;
    } else {
      if (userData) {
        if (["Organization"].includes(userData.ref_type)) {
          submitting_practice = (values.doctor_attributes as CaseDoctorIOveraProps).submitting_practice_id;
        } else {
          if (["Practice", "Contact"].includes(userData.ref_type)) {
            if (practiceDropdownApiRes && practiceDropdownApiRes.data && practiceDropdownApiRes.data.length > 0)
              submitting_practice = practiceDropdownApiRes.data[0].id
          } else if (["GlobalDoctor"].includes(userData.ref_type)) {
            if (["Treating Provider"].includes(i_am))
              submitting_practice = (values.doctor_attributes as CaseDoctorIOveraProps).treating_practice_id
            else
              submitting_practice = (values.doctor_attributes as CaseDoctorIOveraProps).referring_practice_id
          } else {
            if (["Treating Facility", "Treating Provider", "Referring and Treating Facility"].includes(i_am))
              submitting_practice = (values.doctor_attributes as CaseDoctorIOveraProps).treating_practice_id
            else if (["Referring Facility", "Referring Provider"].includes(i_am))
              submitting_practice = (values.doctor_attributes as CaseDoctorIOveraProps).referring_practice_id
          }
        }
      }
    }

    if (submitting_practice) {
      case_attributes_attributes.push({
        name: "submitting_practice",
        value: submitting_practice
      })
    }

     const {is_us_citizen,is_enrolled_in_federal, ...actualPatientAttributes }  = values.patient_attributes as PatientAttributes
    

    const patient_attributes_attributes = []
    if (typeof is_us_citizen === "boolean") {
      patient_attributes_attributes.push({
        name: "is_us_citizen",
        value: is_us_citizen ? "true" : "false"
      })
    }
    if (typeof is_enrolled_in_federal === "boolean") {
      patient_attributes_attributes.push({
        name: "is_enrolled_in_federal",
        value: is_enrolled_in_federal ? "true" : "false"
      })
    }
    if (values.prescription_attributes.drug_name.toLowerCase().includes("zil")) {
      patient_attributes_attributes.push({
        name: "copay_program",
        value: "false"
      })
    }

    const newValues = {
      ...values,
      ...(values.prescription_attributes.drug_name.toLowerCase().includes("zil") ? { category: values.category || "med" } : { category: "med" }),
      drug_name: values.prescription_attributes.drug_name,
      ...(values.procedure_attributes ? {
        procedures_attributes: [
          { cpt_code: values.procedure_attributes.cpt_code },
        ]
      } : {}),
      prescription_attributes: {
        ...actualPrescription,
        drug_name: values.prescription_attributes.drug_name,
        drug_id: values.prescription_attributes.drug_name === "iovera°" || values.prescription_attributes.drug_name === "iovera" ? 14 : 1,
        // nerves,
        injection_site: values.prescription_attributes.injection_site,
        diagnosis_code: values.prescription_attributes.diagnosis_code.join(","),
        // ndc: dataProduct.find((drug) => values.prescription_attributes.drug_name === drug.value)?.ndc || "",
        prescription_attributes_attributes,
      },
      // shipments_attributes,
      // shipments_attributes_attributes,
      patient_firstlastname: `${(values.patient_attributes as PatientAttributes).firstname} ${(values.patient_attributes as PatientAttributes).lastname}`,
      patient_fullname: `${(values.patient_attributes as PatientAttributes).firstname} ${(values.patient_attributes as PatientAttributes).middlename} ${(values.patient_attributes as PatientAttributes).lastname}`,
      patient_dob: (values.patient_attributes as PatientAttributes).dob,
      insurances_attributes: values.insurances_attributes?.map((insurance, index) => {
        insurance.insurance_priority = index;
        return insurance;
      }),
      ...(values.prescription_attributes.drug_name === "iovera°" ?
        {
          ...(values.doctor_attributes as CaseDoctorIOveraProps).is_anesthesiologist === true ?
            { practice_name: values.practice_name, } :
            {
              practice_name: values.practice_name,
              doctor_fullname: values.doctor_fullname,
              doctor_firstlastname: values.doctor_firstlastname,
            },
          doctor_attributes: {
            ...(values.doctor_attributes as CaseDoctorIOveraProps).is_anesthesiologist === true ?
              { practice_id: (values.doctor_attributes as CaseDoctorIOveraProps).treating_practice_id || 0, } :
              {
                practice_id: (values.doctor_attributes as CaseDoctorIOveraProps).treating_practice_id || 0,
                global_doctor_id: (values.doctor_attributes as CaseDoctorIOveraProps).treating_doctor_id || 0,
              }
          }
        } :
        { doctor_attributes: values.doctor_attributes }
      ),

      patient_attributes: {
        ...actualPatientAttributes,
        ...(actualPatientAttributes.patient_email && {
          patient_email: {
            id: actualPatientAttributes.patient_email_id,
            email: actualPatientAttributes.patient_email
          }
        }),
        patient_attributes_attributes
      },
      shipments_attributes: values.shipments_attributes || [ { ship_to: "other" } ],
      shipments_attributes_attributes: [],
      case_attributes_attributes,
      ...((values.doctor_attributes as CaseDoctorIOveraProps).is_anesthesiologist === true ? {
        doctor_fullname: "",
        doctor_firstlastname: "",
      } : {}),
    };

    const { procedure_attributes, ...actualCaseValues } = newValues;
    addNewCase({
      case: actualCaseValues,
      ...(values.prescription_attributes.drug_name === "iovera°" ? {
        ...{
          ...actual_doctor_attributes,
          ...((values.doctor_attributes as CaseDoctorIOveraProps).referring_practice_id ? {
            referring_practice_id: (values.doctor_attributes as CaseDoctorIOveraProps).referring_practice_id,
            referring_doctor_id: (values.doctor_attributes as CaseDoctorIOveraProps).referring_doctor_id
          } : {}),
          ...(
            submitting_practice ?
            {submitting_practice_id: submitting_practice} : {}
          )
        },
        ...(
          userData && ["Practice", "Contact", "Organization"].includes(userData.ref_type) ? {
            ...(
                (values.doctor_attributes as CaseDoctorIOveraProps).is_anesthesiologist === true ?
              { treating_provider_as_practice_id: (values.doctor_attributes as CaseDoctorIOveraProps).treating_doctor_id }
              :
              { treating_doctor_id: (values.doctor_attributes as CaseDoctorIOveraProps).treating_doctor_id }
            ),
            ...(!(values.patient_attributes as PatientAttributes).global_patient_id && ["Treating Provider"].includes(i_am) ? 
                { patient_practice_id: submitting_practice } :
                {}
            ),
          } : {
            treating_doctor_id: (values.doctor_attributes as CaseDoctorIOveraProps).treating_doctor_id,
          }
        )
      } : {})
    });
  };

  return {
    addCaseLoading,
    caseDataSubmission
  }
}

export default useCaseSubmitFunc
